/**
 * Executable/container format sniffing on the main thread. Mirrors the C
 * loader's `loader_identify` so the UI can give an immediate, friendly verdict
 * — including the "this file is encrypted, provide a decrypted dump" path —
 * before any bytes reach the core. Voland never decrypts copyrighted content.
 */

export type GameFormat = "nro" | "elf" | "nso" | "nca" | "nsp" | "xci" | "unknown";

export interface FormatInfo {
  readonly format: GameFormat;
  /** Whether the Phase 1 core can load it (only homebrew NRO for now). */
  readonly loadable: boolean;
  /** Human-readable explanation, shown in the boot log / UI. */
  readonly reason: string;
}

function ascii(bytes: Uint8Array, offset: number, length: number): string {
  if (offset + length > bytes.length) return "";
  let s = "";
  for (let i = 0; i < length; i++) s += String.fromCharCode(bytes[offset + i]);
  return s;
}

export function sniffFormat(bytes: Uint8Array): FormatInfo {
  if (bytes.length >= 5 && bytes[0] === 0x7f && ascii(bytes, 1, 3) === "ELF" && bytes[4] === 2) {
    return { format: "elf", loadable: true, reason: "ELF64 ARM64 binary" };
  }
  if (ascii(bytes, 0x10, 4) === "NRO0") {
    return { format: "nro", loadable: true, reason: "Homebrew NRO" };
  }
  if (ascii(bytes, 0, 4) === "NSO0") {
    return {
      format: "nso",
      loadable: false,
      reason: "NSO executables are not supported yet (Phase 1 loads homebrew NRO).",
    };
  }
  if (ascii(bytes, 0, 4) === "PFS0") {
    return {
      format: "nsp",
      loadable: false,
      reason: "NSP is an encrypted container. Provide a decrypted dump — see docs/DUMP.md.",
    };
  }
  if (ascii(bytes, 0x100, 4) === "HEAD") {
    return {
      format: "xci",
      loadable: false,
      reason: "XCI cartridge images are encrypted. See docs/DUMP.md.",
    };
  }
  if (ascii(bytes, 0x200, 3) === "NCA") {
    return {
      format: "nca",
      loadable: false,
      reason: "NCA loading is not supported yet; a decrypted dump is required.",
    };
  }
  return {
    format: "unknown",
    loadable: false,
    reason: "Unrecognized file. Voland (Phase 1) loads homebrew NRO files.",
  };
}

/**
 * The result of inspecting a dropped/fetched file: either hand it to the core,
 * or reject it with a user-facing message (encrypted containers get pointed at
 * the dumping guide, never decrypted — §1.6). This is the single decision the
 * drag-drop and auto-load paths both make; keeping it pure makes the routing
 * verifiable headlessly (test/load-routing.test.mjs) instead of buried in DOM
 * event handlers.
 */
export type LoadDecision =
  | { readonly action: "load";   readonly format: GameFormat; readonly reason: string }
  | { readonly action: "reject"; readonly format: GameFormat; readonly message: string };

/**
 * Decide what to do with the bytes of a file named `name`. Loadable images
 * (homebrew NRO / ELF) become a "load" action; everything else — encrypted
 * NSP/XCI/NCA, unsupported NSO, and unrecognized files — becomes a "reject"
 * carrying the friendly reason from {@link sniffFormat}. No bytes are decrypted.
 */
export function decideLoad(bytes: Uint8Array, name: string): LoadDecision {
  const info = sniffFormat(bytes);
  if (info.loadable) {
    return { action: "load", format: info.format, reason: info.reason };
  }
  return { action: "reject", format: info.format, message: `cannot load ${name}: ${info.reason}` };
}
